// import libraries
import { doc, setDoc, getDoc, getFirestore } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { SHOP_TYPES } from '../../../constants/shopTypes';
import { DAYS_OF_WEEK } from '../../../components/forms/BusinessHoursForm/constant';
import { convertToWebP } from '../../../utils/imageUpload';
import moment from 'moment';

export async function updateUserProfileDoc(uid, values) {
  if (!uid) throw new Error('updateUserProfileDoc requires uid');
  const db = getFirestore();
  const ref = doc(db, 'users', uid);

  try {
    // Create a copy of values to avoid mutating the original
    const processedValues = { ...values };
    
    // Handle storeImages upload if present
    if (Array.isArray(processedValues.storeImages)) {
      const uploadPromises = processedValues.storeImages
        .filter(img => img?.file?.objectURL) // Only process images with file data
        .map(async (img) => {
          try {
            // Fetch the blob from objectURL
            const response = await fetch(img.objectURL);
            const blob = await response.blob();
            
            // Upload to storage
            const uploadedImage = await uploadImageToStorage(blob, uid);
            
            // Return the new image object
            return {
              ...img,
              url: uploadedImage.url,
              storagePath: uploadedImage.path,
              uploadedAt: uploadedImage.uploadedAt
            };
          } catch (error) {
            console.error('Error uploading image:', error);
            return img; // Return original if upload fails
          }
        });
      
      // Wait for all uploads to complete
      const uploadedImages = await Promise.all(uploadPromises);
      processedValues.storeImages = uploadedImages;
    }

    // Create payload with all values from the form
    const payload = { 
      ...processedValues, 
      uid, 
      updatedAt: moment().unix(),
      setupCompleted: true
    };


    // Remove any remaining File instances or functions from the payload
    const cleanPayload = JSON.parse(JSON.stringify(payload, (key, value) => {
      if (value instanceof File || typeof value === 'function') {
        return undefined; // Remove file instances and functions
      }
      return value;
    }));
    
    await setDoc(ref, cleanPayload, { merge: true });
    return cleanPayload;
    
  } catch (error) {
    console.error('Error in updateUserProfileDoc:', error);
    throw error;
  }
}

export async function saveBusinessAtAGlanceDoc(uid, values) {
  if (!uid) throw new Error('saveBusinessAtAGlanceDoc requires uid');
  const db = getFirestore();
  const ref = doc(db, 'businessAtAGlance', uid);

  // Conform to businessAtAGlance update allowed keys in rules
  const payload = {
    uid,
    shopName: values.shopName || '',
    contactEmail: values.contactEmail || '',
    markerPosition: values.markerPosition || null,
    storeAddress: values.storeAddress || '',
    directions: values.directions || '',
    storeImage: values.storeImage || null,
    setupCompleted: true,
    updatedAt: moment().unix(),
  };

  await setDoc(ref, payload, { merge: true });
  return payload;
}

// ===== Business Config (Second Page) =====

async function uploadImageToStorage(imageFile, uid, path = 'store-images') {
  if (!imageFile) return null;
  
  const storage = getStorage();
  const timestamp = Date.now();
  const fileName = `store-${timestamp}.webp`;
  const storagePath = `${path}/${uid}/${fileName}`;
  
  // Convert to WebP if it's an image
  const webpBlob = await convertToWebP(imageFile);
  
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, webpBlob, { contentType: 'image/webp' });
  const downloadURL = await getDownloadURL(storageRef);
  
  return {
    url: downloadURL,
    path: storagePath,
    name: fileName,
    uploadedAt: Date.now()
  };
}

async function cleanupUnusedImages(existingImages, newValues) {
  if (!existingImages) return;
  const storage = getStorage();
  const promises = [];

  const currentImagePaths = new Set();
  Object.entries(newValues).forEach(([key, value]) => {
    if (key.startsWith('paymentImages_') && value) {
      Object.values(value).forEach((img) => {
        if (img?.path) currentImagePaths.add(img.path);
      });
    }
  });

  Object.values(existingImages).forEach((imageData) => {
    if (imageData?.path && !currentImagePaths.has(imageData.path)) {
      try {
        const imageRef = ref(storage, imageData.path);
        promises.push(deleteObject(imageRef).catch(() => {}));
      } catch (_) {
        // ignore
      }
    }
  });

  await Promise.all(promises);
}

export async function saveBusinessConfig(uid, values) {
  if (!uid) throw new Error('saveBusinessConfig requires uid');

  // Clone values to avoid mutation
  const valuesToProcess = { ...values };

  // Handle payment method image uploads
  const paymentImagePromises = [];
  Object.keys(valuesToProcess).forEach((key) => {
    if (key.startsWith('paymentImages_') && valuesToProcess[key]) {
      const paymentImages = valuesToProcess[key];
      Object.entries(paymentImages).forEach(([method, imageData]) => {
        if (imageData?.file && imageData.isNew) {
          paymentImagePromises.push(
            uploadImageToStorage(imageData.file, uid, 'payment-methods')
              .then(({ url, path }) => {
                valuesToProcess[key] = {
                  ...valuesToProcess[key],
                  [method]: {
                    url,
                    path,
                    name: imageData.name || `payment-${method}`,
                  },
                };
              })
              .catch(() => Promise.resolve())
          );
        }
      });
    }
  });
  await Promise.all(paymentImagePromises);

  // Transform values into the desired structure by category and subtype
  const transformedData = {
    id: uid,
    updatedAt: moment().unix(),
  };

  const selectedShopTypes = Array.isArray(valuesToProcess.shopType)
    ? valuesToProcess.shopType
    : [];

  selectedShopTypes.forEach((shopType) => {
    const category = SHOP_TYPES.find((category) =>
      category.items.some((item) => item.value === shopType)
    );
    if (!category) return;

    if (!transformedData[category.label]) transformedData[category.label] = [];

    const serviceData = {
      [shopType]: {
        // Business hours
        businessHours: {
          days: DAYS_OF_WEEK.map((day) => ({
            openingTime: valuesToProcess[`openingTime_${day.value}_${shopType}`] || '09:00',
            closingTime: valuesToProcess[`closingTime_${day.value}_${shopType}`] || '17:00',
            open: valuesToProcess[`open_${day.value}_${shopType}`] || false,
          })),
          hasSpecialHours: valuesToProcess[`hasSpecialHours_${shopType}`] || false,
          specialHoursDescription: valuesToProcess[`specialHoursDescription_${shopType}`] || '',
          hasBreakTime: valuesToProcess[`hasBreakTime_${shopType}`] || false,
          breakStart: valuesToProcess[`breakStart_${shopType}`] || '12:00',
          breakEnd: valuesToProcess[`breakEnd_${shopType}`] || '13:00',
        },

        // Payment methods
        paymentMethods: valuesToProcess[`paymentMethods_${shopType}`] || [],
        paymentUrls: valuesToProcess[`paymentUrls_${shopType}`] || {},
        paymentImages: valuesToProcess[`paymentImages_${shopType}`] || {},

        // Other subtype-specific fields
        ...Object.keys(values).reduce((acc, key) => {
          const shopConfigFields = [
            'contactEmail', 'contactPhone', 'createdAt', 'directions', 'email',
            'isActive', 'lastActivity', 'lastLogin', 'markerPosition', 'setupCompleted',
            'shopName', 'shopType', 'storeAddress', 'storeImages', 'uid', 'updatedAt',
            'isMainUser', 'assignedFeature',
          ];

          const businessHoursFields = [
            'openingTime_', 'closingTime_',
            'hasSpecialHours_', 'specialHoursDescription_',
            'hasBreakTime_', 'breakStart_', 'breakEnd_',
          ].some((prefix) => key.startsWith(prefix + shopType));

          const isOpenField = key.startsWith('open_');

          if (
            key.includes(`_${shopType}`) &&
            !shopConfigFields.includes(key.split(`_${shopType}`)[0]) &&
            !businessHoursFields &&
            !isOpenField &&
            !key.startsWith('paymentImages_')
          ) {
            acc[key.split(`_${shopType}`)[0]] = valuesToProcess[key];
          }
          return acc;
        }, {}),
      },
    };

    transformedData[category.label].push(serviceData);
  });

  // Save to Firestore
  const db = getFirestore();
  const businessDocRef = doc(db, 'businesses', uid);
  await setDoc(
    businessDocRef,
    { userUid: uid, shopType: transformedData },
    { merge: true }
  );

  // Cleanup unused images from existing doc
  try {
    const existingDoc = await getDoc(businessDocRef);
    const existingData = existingDoc.data();
    if (existingData?.shopType) {
      for (const category of Object.values(existingData.shopType)) {
        if (Array.isArray(category)) {
          for (const item of category) {
            for (const shopTypeData of Object.values(item)) {
              if (shopTypeData.paymentImages) {
                const currentPaymentImages = {};
                Object.keys(valuesToProcess)
                  .filter((key) => key.startsWith('paymentImages_'))
                  .forEach((key) => {
                    currentPaymentImages[key] = valuesToProcess[key];
                  });
                await cleanupUnusedImages(
                  shopTypeData.paymentImages,
                  currentPaymentImages
                );
              }
            }
          }
        }
      }
    }
  } catch (_) {
    // best-effort cleanup
  }

  return true;
}
